import { useEffect, useState } from "react";
import { useQueryClient } from "react-query";

import {
  useDeleteGlobalData,
  useGlobalDataList,
  useSyncGlobalDriveFolder,
  useUpdateGlobalDriveFolder,
} from "../api";
import type { GlobalDataRecord } from "../api";
import { isOperationFinished, useAsyncOperation } from "../api/hooks/useAsyncOperation";
import {
  GLOBAL_DATA_TREE,
  useDriveFolderTree,
  useLoadDriveFolderChildren,
} from "../api/hooks/useDriveSync";
import { QK } from "../api/queryKeys";
import ds from "../components/DriveSyncSettingsCard.module.css";
import { FolderTree } from "../components/FolderTree";
import { ProgressBar } from "../components/ProgressBar";
import { SyncStatusLine } from "../components/SyncStatusLine";
import { describeFileCounts } from "../utils/describeFileCounts";
import { driveOpenUrl } from "../utils/driveOpenUrl";
import { sameMembers } from "../utils/sameMembers";
import { describeAdded, describeScope } from "./globalDataLabels";
import { GlobalDriveFolderFormDialog } from "./GlobalDriveFolderFormDialog";
import type { FolderSettings } from "./GlobalDriveFolderFormDialog";
import folderStyles from "./GlobalDriveFolder.module.css";
import { Chevron } from "../components/Chevron";

const messageOf = (err: unknown): string =>
  err instanceof Error ? err.message : String(err);

const splitIds = (key: string): string[] => (key ? key.split(",") : []);

interface Props {
  record: GlobalDataRecord;
  connected: boolean;
}

export const GlobalDriveFolderRow = ({ record, connected }: Props) => {
  const globalDataId = record.global_data_id;
  const driveSync = record.drive_sync;
  const queryClient = useQueryClient();
  const deleteOne = useDeleteGlobalData();
  const update = useUpdateGlobalDriveFolder();
  const triggerSync = useSyncGlobalDriveFolder();
  const [pendingOpKey, setPendingOpKey] = useState<string | null>(null);
  // The list can still name a sync this row watched finish until it refetches.
  const [finishedOpKey, setFinishedOpKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const awaitingReview = driveSync?.awaiting_review ?? false;
  const [expanded, setExpanded] = useState(awaitingReview);
  const [editing, setEditing] = useState(false);
  // Keyed on the ids' text so a list refetch mid-edit doesn't discard unsaved ticks.
  const savedExcludedKey = (driveSync?.excluded_subfolder_ids ?? []).join(",");
  const [excluded, setExcluded] = useState<Set<string>>(
    () => new Set(splitIds(savedExcludedKey)),
  );
  const savedExcludedFilesKey = Object.keys(driveSync?.excluded_drive_file_ids ?? {}).join(",");
  const [excludedFiles, setExcludedFiles] = useState<Set<string>>(
    () => new Set(splitIds(savedExcludedFilesKey)),
  );
  const syncOp = useAsyncOperation(pendingOpKey, { enabled: pendingOpKey !== null });
  const syncing = pendingOpKey !== null;
  const tree = useDriveFolderTree(
    driveSync?.folder_id,
    expanded && connected,
    GLOBAL_DATA_TREE,
    true,
  );
  const loadChildren = useLoadDriveFolderChildren(GLOBAL_DATA_TREE, true);

  useGlobalDataList({ poll: syncing });

  useEffect(() => {
    setExcluded(new Set(splitIds(savedExcludedKey)));
  }, [savedExcludedKey]);

  useEffect(() => {
    setExcludedFiles(new Set(splitIds(savedExcludedFilesKey)));
  }, [savedExcludedFilesKey]);

  const activeOpKey = record.active_sync_operation_key ?? null;
  useEffect(() => {
    if (activeOpKey && pendingOpKey === null && activeOpKey !== finishedOpKey) {
      setPendingOpKey(activeOpKey);
    }
  }, [activeOpKey, pendingOpKey, finishedOpKey]);

  useEffect(() => {
    if (!syncOp.data) return;
    const { status, error: opError } = syncOp.data;
    if (!isOperationFinished(status)) return;
    void queryClient.invalidateQueries([QK.globalDataList]);
    void queryClient.invalidateQueries([QK.globalDriveFiles]);
    if (status === "failed") setError(`Sync failed: ${opError ?? "unknown error"}`);
    setFinishedOpKey((prev) => pendingOpKey ?? prev);
    setPendingOpKey(null);
  }, [syncOp.data, queryClient, pendingOpKey]);

  const startSync = async () => {
    const resp = await triggerSync.mutateAsync({ global_data_id: globalDataId });
    setPendingOpKey(resp.operation_key);
  };

  const onSyncNow = async () => {
    setError(null);
    try {
      await startSync();
    } catch (e) {
      setError(`Starting the sync failed: ${messageOf(e)}`);
    }
  };

  const save = async (settings: FolderSettings, excludedIds: string[], fileIds: string[]) => {
    const savedFiles = splitIds(savedExcludedFilesKey);
    await update.mutateAsync({
      global_data_id: globalDataId,
      ...settings,
      excluded_subfolder_ids: excludedIds,
      exclude_file_ids: fileIds.filter((id) => !savedFiles.includes(id)),
      include_file_ids: savedFiles.filter((id) => !fileIds.includes(id)),
      names: toggledNames,
    });
    const exclusionsChanged =
      !driveSync ||
      !sameMembers(settings.exclude_name_patterns, driveSync.exclude_name_patterns) ||
      !sameMembers(excludedIds, driveSync.excluded_subfolder_ids) ||
      !sameMembers(fileIds, splitIds(savedExcludedFilesKey));
    const syncTurnedOn = settings.enabled && !(driveSync?.enabled ?? false);
    // A folder awaiting review syncs only when someone presses Start first sync.
    if (awaitingReview || !connected || (!exclusionsChanged && !syncTurnedOn)) return;
    try {
      await startSync();
    } catch (e) {
      throw new Error(`Saved, but starting the sync failed: ${messageOf(e)}`);
    }
  };

  const onSaveExclusions = async () => {
    setError(null);
    try {
      await save(
        {
          title: record.title,
          tenant_scope: record.tenant_scope,
          landlord_scope: record.landlord_scope,
          exclude_name_patterns: driveSync?.exclude_name_patterns ?? [],
          enabled: driveSync?.enabled ?? true,
        },
        Array.from(excluded),
        Array.from(excludedFiles),
      );
    } catch (e) {
      setError(messageOf(e));
    }
  };

  const onRemove = () => {
    const ok = window.confirm(
      `Remove global folder "${record.title}"?\n\nIts synced files leave global data, so no project's agents see them any more. Nothing in Google Drive is changed.`,
    );
    if (!ok) return;
    deleteOne.mutate(globalDataId);
  };

  const [toggledNames, setToggledNames] = useState<Record<string, string>>({});
  const noteName = (id: string, name: string) =>
    setToggledNames((prev) => (prev[id] === name ? prev : { ...prev, [id]: name }));

  const toggleExcludedFile = (id: string, name: string) => {
    noteName(id, name);
    setExcludedFiles((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleExcluded = (id: string, name: string) => {
    noteName(id, name);
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const syncBusy = syncing || triggerSync.isLoading;
  const excludedCount = driveSync?.excluded_subfolder_ids.length ?? 0;
  const exclusionsDirty =
    !sameMembers(Array.from(excluded), splitIds(savedExcludedKey)) ||
    !sameMembers(Array.from(excludedFiles), splitIds(savedExcludedFilesKey));
  const excludedFileCount = splitIds(savedExcludedFilesKey).length;
  const added = describeAdded(
    driveSync?.added_by?.name || driveSync?.added_by?.email,
    driveSync?.added_at,
  );

  return (
    <li className={ds.sourceRowBlock}>
      <div className={ds.sourceRow}>
        <button
          type="button"
          className={ds.chevron}
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          aria-label={expanded ? "Hide folder structure" : "Show folder structure"}
        >
          <Chevron tree open={expanded} />
        </button>
        <div className={ds.sourceMain}>
          <span className={ds.sourceName}>
            <strong>{record.title}</strong>
            {awaitingReview ? (
              <span className={folderStyles.pausedPill}>not synced yet</span>
            ) : (
              driveSync &&
              !driveSync.enabled && (
                <span className={folderStyles.pausedPill}>auto-sync off</span>
              )
            )}
          </span>
          <span className={ds.sourceMeta}>
            Drive folder &ldquo;{driveSync?.folder_name || record.original_name}&rdquo;
            {excludedCount > 0 &&
              ` · ${excludedCount} subfolder${excludedCount === 1 ? "" : "s"} excluded`}
            {excludedFileCount > 0 &&
              ` · ${excludedFileCount} file${excludedFileCount === 1 ? "" : "s"} excluded`}
          </span>
          {added && <span className={ds.sourceMeta}>{added}</span>}
          <span className={ds.sourceMeta}>
            {describeScope(record.tenant_scope, record.landlord_scope)}
          </span>
          {driveSync && (
            <SyncStatusLine
              lastSuccessfulSyncAt={driveSync.last_successful_sync_at}
              lastSyncedAt={driveSync.last_synced_at}
              lastSyncStatus={driveSync.last_sync_status}
              lastSyncError={driveSync.last_sync_error}
              walkTruncated={driveSync.last_sync_walk_truncated}
              newFolders={driveSync.last_sync_new_folders ?? []}
            />
          )}
          {record.file_counts && (
            <span className={ds.sourceMeta}>{describeFileCounts(record.file_counts)}</span>
          )}
        </div>
        <div className={folderStyles.rowActions}>
          {driveSync && (
            <a
              className={ds.linkButton}
              href={driveOpenUrl(driveSync.folder_id)}
              target="_blank"
              rel="noreferrer"
            >
              Open in Drive
            </a>
          )}
          <button type="button" className={ds.linkButton} onClick={() => setEditing(true)}>
            Edit
          </button>
          <button
            type="button"
            className={ds.secondaryButton}
            onClick={() => void onSyncNow()}
            disabled={syncBusy || !connected || (awaitingReview && exclusionsDirty)}
            title={
              !connected
                ? "Connect a shared drive to sync"
                : awaitingReview && exclusionsDirty
                  ? "Save your exclusions first"
                  : undefined
            }
          >
            {syncBusy ? "Syncing…" : awaitingReview ? "Start first sync" : "Sync now"}
          </button>
          <button
            type="button"
            className={ds.dangerButton}
            onClick={onRemove}
            disabled={deleteOne.isLoading}
          >
            Remove
          </button>
        </div>
      </div>

      {syncing && syncOp.data?.progress && (
        <div className={ds.sourceTree}>
          <ProgressBar progress={syncOp.data.progress} />
        </div>
      )}
      {error && (
        <div className={ds.sourceTree}>
          <p className={ds.error} role="alert">
            {error}
          </p>
        </div>
      )}

      {expanded && (
        <div className={ds.sourceTree}>
          {!connected && (
            <p className={ds.muted}>
              Connect global data to a shared drive to change the subfolder exclusions.
            </p>
          )}
          {tree.isLoading && <p className={ds.muted}>Loading folder tree from Drive…</p>}
          {tree.isError && (
            <p className={ds.error}>
              Couldn&apos;t load the folder preview.{" "}
              <button
                type="button"
                onClick={() => void tree.refetch()}
                className={ds.linkButton}
              >
                Retry
              </button>
            </p>
          )}
          {tree.data && (
            <>
              <p className={ds.helpText}>
                {awaitingReview
                  ? "Nothing has been downloaded from this folder yet. Untick every subfolder and file global data should not hold (zips are expanded into all their files), save, then press Start first sync."
                  : "Untick a subfolder to exclude it and everything under it, or a file to exclude just that file, then save."}
              </p>
              <FolderTree
                tree={tree.data.tree}
                excludedSubfolderIds={excluded}
                onToggleExcluded={toggleExcluded}
                onLoadChildren={loadChildren}
                excludedFileIds={excludedFiles}
                onToggleExcludedFile={toggleExcludedFile}
              />
              {exclusionsDirty && (
                <div className={ds.actions}>
                  <button
                    type="button"
                    className={ds.primaryButton}
                    onClick={() => void onSaveExclusions()}
                    disabled={update.isLoading}
                  >
                    {update.isLoading ? "Saving…" : "Save exclusions"}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {editing && (
        <GlobalDriveFolderFormDialog
          record={record}
          excludedCount={excluded.size}
          onSave={(settings) => save(settings, Array.from(excluded), Array.from(excludedFiles))}
          onClose={() => setEditing(false)}
        />
      )}
    </li>
  );
};
